import React, { useState, useEffect, lazy, Suspense } from 'react'
import { PAIN_POINTS, TESTIMONIALS } from '@/constants/landingData'

// Lazy load components for better performance
const Navbar = lazy(() => import('@/components/landing/Navbar'))
const HeroSection = lazy(() => import('@/components/landing/HeroSection'))
const PainPointsCarousel = lazy(() => import('@/components/landing/PainPointsCarousel'))
const FeaturesSection = lazy(() => import('@/components/landing/FeaturesSection'))
const HowItWorksSection = lazy(() => import('@/components/landing/HowItWorksSection'))
const PricingSection = lazy(() => import('@/components/landing/PricingSection'))
const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection'))
const CTASection = lazy(() => import('@/components/landing/CTASection'))
const Footer = lazy(() => import('@/components/landing/Footer'))
const MaxChatWidget = lazy(() => import('@/components/MaxChatWidget'))

// Loading fallback
const SectionLoader: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-8 h-8 border-2 border-slate-600 border-t-slate-300 rounded-full animate-spin" />
  </div>
)

const LandingPage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [currentPainPoint, setCurrentPainPoint] = useState(0)

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Autoplay testimonios
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % TESTIMONIALS.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  // Autoplay dolores
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPainPoint((prev) => (prev + 1) % PAIN_POINTS.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  const nextTestimonial = () => setCurrentTestimonial((prev) => (prev + 1) % TESTIMONIALS.length)
  const prevTestimonial = () => setCurrentTestimonial((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)

  const nextPainPoint = () => setCurrentPainPoint((prev) => (prev + 1) % PAIN_POINTS.length)
  const prevPainPoint = () => setCurrentPainPoint((prev) => (prev - 1 + PAIN_POINTS.length) % PAIN_POINTS.length)

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      <Suspense fallback={<SectionLoader />}>
        <Navbar isScrolled={isScrolled} onScrollToSection={scrollToSection} />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <HeroSection onScrollToSection={scrollToSection} />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <PainPointsCarousel
          currentIndex={currentPainPoint}
          onNext={nextPainPoint}
          onPrev={prevPainPoint}
          onSelect={setCurrentPainPoint}
        />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <FeaturesSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <HowItWorksSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <PricingSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <TestimonialsSection
          currentIndex={currentTestimonial}
          onNext={nextTestimonial}
          onPrev={prevTestimonial}
          onSelect={setCurrentTestimonial}
        />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <CTASection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Footer onScrollToSection={scrollToSection} />
      </Suspense>

      <Suspense fallback={null}>
        <MaxChatWidget />
      </Suspense>
    </div>
  )
}

export default LandingPage
